import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSessionStore } from '@stores/sessionStore';
import { useTranslation } from '@stores/translationStore';
import { useNavigationStore } from '@stores/navigationStore';
import { useSignalR } from '@hooks/useSignalR';
import { apiClient } from '@services/api';
import { resolveDisplayValue } from '../i18n';
import { playSound } from '../services/soundService';

// Map of route segments to title translation keys
const PAGE_TITLES: Record<string, { key: string; fallback: string }> = {
  housekeeping: { key: 'nav.housekeeping', fallback: 'Housekeeping' },
  maintenance:  { key: 'nav.maintenance',  fallback: 'Maintenance' },
  guides:       { key: 'nav.guides',       fallback: 'Guides' },
  messages:     { key: 'nav.messages',     fallback: 'Messages' },
  notifications:{ key: 'nav.activity', fallback: 'My Activity' },
  vouchers:     { key: 'nav.vouchers',     fallback: 'Vouchers' },
  profile:      { key: 'profile.title',    fallback: 'My profile' },
  'register-rooms': { key: 'rooms.title',  fallback: 'Register your rooms' },
  'my-rooms':     { key: 'rooms.myTitle',  fallback: 'My rooms' },
  station:      { key: 'nav.menu',         fallback: 'Menu' },
  order:        { key: 'nav.orderStatus',  fallback: 'Order Status' },
};

export default function TopNavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hotelSlug: paramSlug } = useParams<{ hotelSlug: string }>();
  const { session } = useSessionStore();
  const { t } = useTranslation();
  const { getPageTitle: getCustomTitle } = useNavigationStore();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const { on } = useSignalR();

  // Determine if we're on the welcome (home) page
  const isWelcomePage = location.pathname.endsWith('/welcome') || location.pathname === '/' || location.pathname === `/${paramSlug}` || location.pathname === `/${paramSlug}/`;

  // Load unread counts
  const loadUnreadCounts = useCallback(async () => {
    try {
      const [msgRes, notifRes] = await Promise.all([
        apiClient.get('/api/v1/guest/messages/unread-count'),
        apiClient.get('/api/v1/guest/notifications/unread-count'),
      ]);
      setUnreadMessages(msgRes.data.count || 0);
      setUnreadNotifications(notifRes.data.count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadUnreadCounts();
  }, [loadUnreadCounts]);

  // Listen for new messages and notifications
  useEffect(() => {
    const unsubMsg = on('NewMessage', (data: unknown) => {
      // A voice call's transcript arrives turn by turn while the guest is on the call: they heard it
      // already, and a ding mid-call would play into their microphone.
      if ((data as { channel?: string } | null)?.channel === 'Voice') return;
      if (!location.pathname.includes('/messages')) {
        setUnreadMessages(prev => prev + 1);
        playSound('message-received');
      }
    });
    const unsubNotif = on('NotificationReceived', () => {
      if (!location.pathname.includes('/notifications')) {
        setUnreadNotifications(prev => prev + 1);
        playSound('notification');
      }
    });
    return () => { unsubMsg(); unsubNotif(); };
  }, [on, location.pathname]);

  // Clear badge when navigating to the page
  useEffect(() => {
    if (location.pathname.includes('/messages')) setUnreadMessages(0);
    if (location.pathname.includes('/notifications')) setUnreadNotifications(0);
  }, [location.pathname]);

  // Get hotel name for welcome header
  const getHotelName = (): string => {
    return resolveDisplayValue(session?.hotelName, t) || '';
  };

  // Get the current inner-page title
  const getInnerPageTitle = (): string => {
    // Check for a custom title set by the page itself (e.g., station name)
    const custom = getCustomTitle(location.pathname);
    if (custom) return custom;

    // Match against known page segments
    for (const [segment, { key, fallback }] of Object.entries(PAGE_TITLES)) {
      if (location.pathname.includes(`/${segment}`)) {
        const translated = t(key);
        return translated !== key ? translated : fallback;
      }
    }
    return '';
  };

  const navigateTo = (path: string) => {
    if (paramSlug) {
      navigate(`/${paramSlug}/${path}`);
    } else {
      navigate(`/${path}`);
    }
  };

  const handleBack = () => {
    // Try browser back first; if no history, go to welcome
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigateTo('welcome');
    }
  };

  // ── Welcome page header: full icons ──
  if (isWelcomePage) {
    return (
      <header className="top-nav-bar safe-area-top">
        <div className="top-nav-content">
          {/* Left side - Notifications & Messages */}
          <div className="top-nav-left">
            <button 
              className="top-nav-icon-btn"
              onClick={() => navigateTo('notifications')}
              aria-label={t('nav.notifications') || 'Notifications'}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadNotifications > 0 && (
                <span className="top-nav-badge">{unreadNotifications > 99 ? '99+' : unreadNotifications}</span>
              )}
            </button>
            <button 
              className="top-nav-icon-btn"
              onClick={() => navigateTo('messages')}
              aria-label={t('nav.messages') || 'Messages'}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              {unreadMessages > 0 && (
                <span className="top-nav-badge">{unreadMessages > 99 ? '99+' : unreadMessages}</span>
              )}
            </button>
          </div>

          {/* Center - Hotel name */}
          <div className="top-nav-title">
            <h1>{getHotelName()}</h1>
          </div>

          {/* Right side - Profile (this bar is the welcome page's, so no Home button) */}
          <div className="top-nav-right">
            {session?.deviceTrusted === true && (
              <button
                className="top-nav-icon-btn"
                onClick={() => navigateTo('profile')}
                aria-label={t('nav.account') || 'Account'}
                title={t('profile.title') || 'My profile'}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </header>
    );
  }

  // ── Inner page header: back arrow + centred title ──
  return (
    <header className="top-nav-bar top-nav-bar--inner safe-area-top">
      <div className="top-nav-content">
        {/* Left: back button */}
        <div className="top-nav-left">
          <button
            className="top-nav-icon-btn top-nav-back-btn"
            onClick={handleBack}
            aria-label={t('nav.back') || 'Back'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>

        {/* Center: page title */}
        <div className="top-nav-title">
          <h1>{getInnerPageTitle()}</h1>
        </div>

        {/* Right: the page's own actions (portalled in by the page), else a placeholder to keep the title centred */}
        <div className="top-nav-right" id="top-nav-actions" style={{ minWidth: 36 }} />
      </div>
    </header>
  );
}
